import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import AppSnackbar from '../../../common/AppSnackbar';
import { useDebounce } from '../../../common/Debounce.hooks';
import { useFindUsers } from '../../../user/User.hooks';
import { User } from '../../../user/User.interface';
import { useUpdateFile } from '../Document.hook';
import { FileDto } from '../Document.interface';

export interface AssignDocumentOwnerDialogProps {
  document: FileDto;
  onCancel?: () => void;
  onAssigned: (document: Partial<FileDto>) => void;
}

const AssignDocumentOwnerDialog: React.FC<AssignDocumentOwnerDialogProps> = (
  props: AssignDocumentOwnerDialogProps
) => {
  const { document, onCancel, onAssigned } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);
  const debouncedSearch = useDebounce(search || '', 300);
  const previousSearch = useRef('');
  const [selectedUser, setSelectedUser] = useState(document.ownedBy);
  const [
    { data: updatedFile, isLoading: isUpdating, error: updateError },
    updateDocument
  ] = useUpdateFile(document.id);
  const { userList, isLoading, error, get } = useFindUsers();

  useEffect(() => {
    // handle searching
    if (debouncedSearch !== previousSearch.current) {
      previousSearch.current = debouncedSearch;
      setPage(0);
    }
    const params = {
      filter: debouncedSearch,
      page,
      limit
    } as any;
    get(params);
  }, [debouncedSearch, get, limit, page]);

  const isSelected = useCallback(
    (user: User) => {
      // check if a user is selected
      return selectedUser ? selectedUser.id === user.id : false;
    },
    [selectedUser]
  );

  const filteredUsers = useMemo(() => {
    // remove any selected user since we display selected user
    // above the user list
    if (!userList) return [];
    return userList.data.filter(user => !isSelected(user));
  }, [isSelected, userList]);

  useEffect(() => {
    updatedFile && onAssigned(updatedFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedFile]);

  const renderRows = (users: User[]) => {
    return users.map(user => (
      <TableRow
        key={user.id}
        hover
        role='checkbox'
        color='primary'
        selected={isSelected(user)}
        onClick={() => {
          if (isSelected(user)) {
            setSelectedUser(undefined);
          } else {
            setSelectedUser(user);
          }
        }}
      >
        <TableCell padding='checkbox'>
          <Checkbox color='primary' checked={isSelected(user)} />
        </TableCell>
        <TableCell>{user.firstName}</TableCell>
        <TableCell>{user.lastName}</TableCell>
        <TableCell>
          {user.groups?.map(group => group.name).join(', ')}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Dialog open onClose={onCancel}>
      <DialogTitle>Assign Document Owner</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Search for user that you would like to assign
        </DialogContentText>
        <Box mt={1} mb={2}>
          <form autoComplete='off' onSubmit={event => event.preventDefault()}>
            <TextField
              variant='outlined'
              autoFocus
              fullWidth
              autoComplete='off'
              type='text'
              name='name'
              label='Employee Name'
              onChange={event => setSearch(event.target.value)}
            />
          </form>
        </Box>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Team</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {renderRows(selectedUser ? [selectedUser] : [])}
                {renderRows(filteredUsers)}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={userList?.total || 0}
            rowsPerPage={limit}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
          <AppSnackbar severity='error' open={Boolean(error)}>
            There was an error loading the list of users.
          </AppSnackbar>
        </Card>
        {updateError && (
          <Alert severity='error'>
            Error occurred.{' '}
            {updateError?.message ||
              'Please check your internet connection and try again.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant='contained'
          color='primary'
          disabled={isLoading || isUpdating || !selectedUser}
          endIcon={isUpdating && <CircularProgress color='primary' size={20} />}
          onClick={() => {
            updateDocument({ ownedBy: selectedUser });
          }}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDocumentOwnerDialog;
